/**
 * PlanProgressSection - Displays plan progress from the task checklist in the sidebar
 *
 * Replays the session's task tool calls and renders a collapsible checklist showing
 * completion status. Hidden entirely when the session has no checklist.
 */

import * as React from 'react'
import { useState, useMemo } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Circle, CircleCheck, ChevronRight } from 'lucide-react'
import { Spinner, aggregateTaskChecklist, type TodoItem, type TodoStatus } from '@craft-agent/ui'
import { useSession } from '@/context/AppShellContext'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TodoStatusIcon({ status, isSessionProcessing }: { status: TodoStatus; isSessionProcessing: boolean }) {
  switch (status) {
    case 'pending':
      return <Circle className="w-3.5 h-3.5 shrink-0 text-muted-foreground/50" />
    case 'in_progress':
      return isSessionProcessing ? (
        <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
          <Spinner className="text-[10px]" />
        </div>
      ) : (
        <Circle className="w-3.5 h-3.5 shrink-0 text-blue-400" />
      )
    case 'completed':
      return <CircleCheck className="w-3.5 h-3.5 shrink-0 text-accent" />
    case 'interrupted':
      return <Circle className="w-3.5 h-3.5 shrink-0 text-muted-foreground/30" />
  }
}

function TodoRow({ todo, isSessionProcessing }: { todo: TodoItem; isSessionProcessing: boolean }) {
  const displayText =
    todo.status === 'in_progress' && todo.activeForm ? todo.activeForm : todo.content

  return (
    <div
      className={cn(
        'flex items-start gap-2 py-1 px-2 rounded-md text-[13px] text-foreground/80',
        todo.status === 'completed' && 'opacity-50',
      )}
    >
      <div className="mt-0.5">
        <TodoStatusIcon status={todo.status} isSessionProcessing={isSessionProcessing} />
      </div>
      <span className={cn('flex-1 leading-snug', todo.status === 'completed' && 'line-through')}>
        {displayText}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface PlanProgressSectionProps {
  sessionId: string
}

export function PlanProgressSection({ sessionId }: PlanProgressSectionProps) {
  const session = useSession(sessionId)
  const [isExpanded, setIsExpanded] = useState(true)

  const todos = useMemo(() => {
    if (!session?.messages) return []
    return aggregateTaskChecklist(session.messages)
  }, [session?.messages])

  const isProcessing = session?.isProcessing ?? false

  // Hide entirely when the session has no task checklist
  if (todos.length === 0) return null

  const completedCount = todos.filter((t) => t.status === 'completed').length

  return (
    <div className="shrink-0 border-b border-border/50">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="flex items-center gap-1.5 w-full px-4 pt-3.5 pb-2 select-none group"
      >
        <ChevronRight
          className={cn(
            'h-3 w-3 text-muted-foreground/60 transition-transform duration-200',
            isExpanded && 'rotate-90',
          )}
        />
        <span className="text-xs font-medium text-muted-foreground">Progress</span>
        <span className="text-xs text-muted-foreground/60 ml-auto">
          {completedCount}/{todos.length}
        </span>
      </button>

      {/* Collapsible list */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-2 pb-3 max-h-[300px] overflow-y-auto">
              {todos.map((todo, index) => (
                <motion.div
                  key={`${todo.content}-${index}`}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.025 }}
                >
                  <TodoRow todo={todo} isSessionProcessing={isProcessing} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
